import QuoteProductsTable from '../components/QuoteProductsTable';
import { buildQuoteProducts, buildQuotePayload, quoteProductsSchema, quoteFormErrors } from '../utils/quoteProducts';
import { clearQuoteDataCache } from '../utils/quoteRequests';
import React, {useState, useEffect, useMemo} from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Col, Row, Card, Form, Button } from '@themesberg/react-bootstrap';
import axios from "axios";
import { API_URL, END_POINT_QUOTES, END_POINT_PRINCIPAL, END_POINT_ADMIN, END_POINT_BRANCHES, END_POINT_COMPANY, END_POINT_CUSTOMERS, END_POINT_PRODUCT_CATEGORIES } from "../const";
import { useNavigate, Link } from "react-router-dom";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import DatePickerField from '../components/DatePickerField';
import SearchableSelect, {
  FormikSearchableSelect,
  buildSelectOptions,
} from '../components/SearchableSelect';

export const CreateQuoteForm = () => {

  const navigate = useNavigate();

  const rawToken = sessionStorage.getItem('quotetoken');
  const token = rawToken?.replace(/^"|"$/g, '');

  const [principal, setPrincipal] = useState([]);
  const [admin, setAdmin] = useState([]);
  const [branch, setBranch] = useState([]);
  const [company, setCompany] = useState([]);
  const [customer, setCustomers] = useState([]);
  const [product, setProduct] = useState([]);
  const [quotes, setQuotes] = useState([]);

  const getValidationSchema = (quotes) =>
  Yup.object({
    system_quote_no: Yup.string()
      // .required("System quote number is required")
      .test(
        "unique-system-quote-no",
        "This system quote number already exists. Please enter a different one.",
        function (value) {
          if (!value) return true; // skip if empty, required() will catch
          return !quotes.some(
            (quote) => quote.system_quote_no?.toLowerCase() === value.toLowerCase()
          );
        }
      ),
    place: Yup.string().required("Place is required"),
    products: quoteProductsSchema,
    offer_date: Yup.string().required("Offer date is required"),
    expected_date: Yup.string().required("Expected date is required"),
    customer_id: Yup.string()
      .notOneOf(["0"], "Please select a valid Customer")
      .required("Customer is required"),
    company_id: Yup.string()
      .notOneOf(["0"], "Please select a valid Company")
      .required("Company is required"),
    category_id: Yup.string()
      .notOneOf(["0"], "Please select a valid Category")
      .required("Category is required"),
    branch_id: Yup.string()
      .notOneOf(["0"], "Please select a valid Branch")
      .required("Branch is required"),
    principal_id: Yup.string()
      .notOneOf(["0"], "Please select a valid Principal")
      .required("Principal is required"),
    se_id: Yup.string()
      .notOneOf(["0"], "Please select a valid Sales Executive")
      .required("Sales Executive is required"),
    status: Yup.string()
      .notOneOf(["0"], "Please select a valid status")
      .required("Status is required"),
  });

  const formik = useFormik({
    initialValues: {
      system_quote_no: "",
      offer_date: "",
      customer_id: "",
      place: "",
      company_id: "",
      branch_id: "",
      principal_id: "",
      products: buildQuoteProducts(),
      category_id: "",
      se_id: "",
      expected_date: "",
      status: "Live",
      remarks: ""
    },
    enableReinitialize: true,
    // validationSchema: getValidationSchema(quotes), 
    validationSchema: getValidationSchema(quotes),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {

        setSubmitting(true);

        const response = await axios.post(API_URL + END_POINT_QUOTES, buildQuotePayload(values),
          {
            headers: {
              // 'Content-Type': 'multipart/form-data',
              'Accept': 'application/json',
              Authorization: `Bearer ${token}`,
            }
          }
        );
        clearQuoteDataCache();
        showSuccessToast(response.data.message)
        navigate('/quotes')
      } catch (error) {
        if (error.response?.data?.errors) setErrors(quoteFormErrors(error.response.data.errors));
        showErrorToast(error.response?.data?.message || 'Failed to create quote.')
      }finally {
        setSubmitting(false);
      }
    }
  });

  const handleOfferDateChange = (date) => {
    formik.setFieldValue("offer_date", date);
  };

  const handleExpectedDateChange = (date) => {
    formik.setFieldValue("expected_date", date);
  };

  //Quote list
    useEffect(() => {
      const fetchQuotelist = async () => {
        try {
          const response = await axios.get(API_URL + END_POINT_QUOTES, {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${token}`,
            }
          });
          if (response.data.status && Array.isArray(response.data.data)) {
            setQuotes(response.data.data);
          }
        } catch (error) {
          console.error('Error fetching tasks:', error);
        }
      };
      const timeout = setTimeout(() => {
        fetchQuotelist();
      });
      return () => clearTimeout(timeout);
    }, []);
    
  // principal list
    useEffect(() => {
      const fetchPrincipallist = async () => {
        try {
  
          const response = await axios.get(API_URL + END_POINT_PRINCIPAL, {
            headers: {
              // 'Content-Type': 'multipart/form-data',
              'Accept': 'application/json',
              Authorization: `Bearer ${token}`,
            }
          });
  
          if (response.data.status && Array.isArray(response.data.data)) {
            setPrincipal(response.data.data);
          }
  
        } catch (error) {
          console.error('Error fetching principal:', error);
        }
  
      };
  
      const timeout = setTimeout(() => {
        fetchPrincipallist();
      });
      return () => clearTimeout(timeout);
  
    }, []);

    // Admin list
    useEffect(() => {
        const fetchAdminlist = async () => {
          try {
    
            const response = await axios.get(API_URL + END_POINT_ADMIN, {
              headers: {
                // 'Content-Type': 'multipart/form-data',
                'Accept': 'application/json',
                Authorization: `Bearer ${token}`,
              }
            });
    
            if (response.data.status && Array.isArray(response.data.data)) {
              setAdmin(response.data.data);
            }
    
          } catch (error) {
            console.error('Error fetching admin:', error);
          }
    
        };
    
        const timeout = setTimeout(() => {
          fetchAdminlist();
        });
        return () => clearTimeout(timeout);
    
      }, []);
      
    // Branches list  
    useEffect(() => {
        const fetchBranchlist = async () => {
          try {
    
            const response = await axios.get(API_URL + END_POINT_BRANCHES, {
              headers: {
                // 'Content-Type': 'multipart/form-data',
                'Accept': 'application/json',
                Authorization: `Bearer ${token}`,
              }
            });
    
            if (response.data && response.data.status) {
              setBranch(response.data.data);
            }
    
          } catch (error) {
            console.error('Error fetching branch:', error);
            // setIsFetching(false);
          }
    
        };
    
        const timeout = setTimeout(() => {
          fetchBranchlist();
        });
        return () => clearTimeout(timeout);
    
    }, []);

    // company list
    useEffect(() => {
        const fetchCompanylist = async () => {
          try {
    
            const response = await axios.get(API_URL + END_POINT_COMPANY, {
              headers: {
                // 'Content-Type': 'multipart/form-data',
                'Accept': 'application/json',
                Authorization: `Bearer ${token}`,
                
              }
            });
    
            if (response.data?.status && response.data.data) {
              const activeCompanies = response.data.data.filter(company => company.deleted_at === null);
              setCompany(activeCompanies);
            }
    
          } catch (error) {
            console.error('Error fetching company:', error);
          }
    
        };
    
        const timeout = setTimeout(() => {
          fetchCompanylist();
        });
        return () => clearTimeout(timeout);
    
    }, []);

    // customers list
    useEffect(() => {
        const fetchCustomerslist = async () => {
          try {
    
            const response = await axios.get(API_URL + END_POINT_CUSTOMERS, {
              headers: {
                'Accept': 'application/json',
                Authorization: `Bearer ${token}`,
              }
            });
    
            if (response.data.status) {
              setCustomers(response.data.data);
            }
    
          } catch (error) {
            console.error('Error fetching customer:', error);
           
          }
    
        };
    
        const timeout = setTimeout(() => {
          fetchCustomerslist();
        });
        return () => clearTimeout(timeout);
    
      }, []);

    // products list
    useEffect(() => {
        const fetchProductslist = async () => {
          try {
    
            const response = await axios.get(API_URL + END_POINT_PRODUCT_CATEGORIES, {
              headers: {
                'Accept': 'application/json',
                Authorization: `Bearer ${token}`,
              }
            });
    
            if (response.data.status) {
              setProduct(response.data.data);
            }
    
          } catch (error) {
            console.error('Error fetching product:', error);
           
          }
    
        };
    
        const timeout = setTimeout(() => {
          fetchProductslist();
        });
        return () => clearTimeout(timeout);
    
      }, []);

      useEffect(() => {
        formik.setFieldValue("category_id", "0"); 
      }, [formik.values.principal_id]);

      useEffect(() => {
        formik.setFieldValue("branch_id", "");
      }, [formik.values.company_id]);

  const customerOptions = useMemo(() => buildSelectOptions(customer), [customer]);
  const companyOptions = useMemo(
    () => buildSelectOptions(company),
    [company]
  );
  // const branchOptions = useMemo(
  //   () => buildSelectOptions(branch),
  //   [branch]
  // );
  const branchOptions = useMemo(
  () =>
    buildSelectOptions(branch, {
      filter: (br) =>
        Array.isArray(br.companies) &&
        br.companies.some(
          (comp) => String(comp.id) === String(formik.values.company_id)
        ),
    }),
  [branch, formik.values.company_id]
);
  const principalOptions = useMemo(
    () => buildSelectOptions(principal),
    [principal]
  );
  const categoryOptions = useMemo(
    () =>
      buildSelectOptions(product, {
        filter: (cat) => String(cat.principal_id) === String(formik.values.principal_id),
      }),
    [product, formik.values.principal_id]
  );
  const adminOptions = useMemo(() => buildSelectOptions(admin), [admin]);
  const statusOptions = useMemo(
    () =>
      ['Closed', 'Live', 'Lost', 'Won'].map((status) => ({
        value: status,
        label: status,
      })),
    []
  );

  const handleCustomerChange = (selectedCustomerId) => {
    formik.setFieldValue("customer_id", selectedCustomerId);

    const selectedCustomer = customer.find(
      (item) => item.id === Number(selectedCustomerId)
    );

    formik.setFieldValue(
      "place",
      selectedCustomer ? selectedCustomer.address_line1 || "" : ""
    );
  };

  return (
    <Card border="light" className="bg-white shadow-sm mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4"> 
            <h5 className="mb-0">Add Quote</h5> 
            <Link to="/quotes" className="text-decoration-none btn-quote-back d-inline-flex align-items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-arrow-left-circle me-1"><circle cx="12" cy="12" r="10"></circle><polyline points="12 8 8 12 12 16"></polyline><line x1="16" y1="12" x2="8" y2="12"></line></svg>
              Back to Listing
            </Link>
        </div>
        <Form noValidate onSubmit={formik.handleSubmit}>
          <Row>

            <Col sm={4} className="mb-3">
              <Form.Group controlId="system_quote_no">
                <Form.Label>System Quote No.</Form.Label>
                <Form.Control
                  type="text"
                  {...formik.getFieldProps("system_quote_no")}
                  isInvalid={formik.touched.system_quote_no && formik.errors.system_quote_no}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.system_quote_no}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4} className="mb-3">
              <Form.Group controlId="offer_date">
                <Form.Label>Offer Date<span className="text-danger">*</span></Form.Label>
                <DatePickerField
                  id="offer_date"
                  name="offer_date"
                  value={formik.values.offer_date}
                  onChange={handleOfferDateChange}
                  onBlur={() => formik.setFieldTouched('offer_date', true)}
                  isInvalid={formik.touched.offer_date && formik.errors.offer_date}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.offer_date}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col sm={4} className="mb-3">
              <Form.Group controlId="customer_id">
                <Form.Label>Customer<span className="text-danger">*</span></Form.Label>
                <SearchableSelect
                  inputId="customer_id"
                  name="customer_id"
                  options={customerOptions}
                  value={formik.values.customer_id}
                  onChange={handleCustomerChange}
                  onBlur={() => formik.setFieldTouched("customer_id", true)}
                  placeholder="Search customer..."
                  isInvalid={formik.touched.customer_id && !!formik.errors.customer_id}
                  errorMessage={formik.errors.customer_id}
                />
              </Form.Group>
            </Col>

            <Col sm={4} className="mb-3">
              <Form.Group controlId="place">
                <Form.Label>Place <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  {...formik.getFieldProps("place")}
                  value={formik.values.place}
                  readOnly
                  isInvalid={formik.touched.place && formik.errors.place}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.place}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col sm={4} className="mb-3">
              <Form.Group controlId="company_id">
                <Form.Label>Company<span className="text-danger">*</span></Form.Label>
                <FormikSearchableSelect
                  formik={formik}
                  name="company_id"
                  options={companyOptions}
                  placeholder="Search company..."
                />
              </Form.Group>
            </Col>

            <Col sm={4} className="mb-3">
              <Form.Group controlId="branch_id">
                <Form.Label>Branch<span className="text-danger">*</span></Form.Label>
                <FormikSearchableSelect
                  formik={formik}
                  name="branch_id"
                  options={branchOptions}
                  placeholder="Search branch..."
                  isDisabled={!formik.values.company_id || formik.values.company_id === "0"}
                />
              </Form.Group>
            </Col>

            <Col sm={4} className="mb-3">
              <Form.Group controlId="principal_id">
                <Form.Label>Principal<span className="text-danger">*</span></Form.Label>
                <FormikSearchableSelect
                  formik={formik}
                  name="principal_id"
                  options={principalOptions}
                  placeholder="Search principal..."
                />
              </Form.Group>
            </Col>

            <Col sm={4} className="mb-3">
              <Form.Group controlId="category_id">
                <Form.Label>Product Category<span className="text-danger">*</span></Form.Label>
                <FormikSearchableSelect
                  formik={formik}
                  name="category_id"
                  options={categoryOptions}
                  placeholder="Search category..."
                  isDisabled={!formik.values.principal_id || formik.values.principal_id === "0"}
                />
              </Form.Group>
            </Col>

            <Col xs={12} className="mb-3">
                <QuoteProductsTable formik={formik} />
              </Col>

              <Col sm={4} className="mb-3">
              <Form.Group controlId="se_id">
                <Form.Label>User<span className="text-danger">*</span></Form.Label>
                <FormikSearchableSelect
                  formik={formik}
                  name="se_id"
                  options={adminOptions}
                  placeholder="Search user..."
                />
              </Form.Group>
            </Col>

            <Col md={4} className="mb-3">
              <Form.Group controlId="expected_date">
                <Form.Label>Expected Date<span className="text-danger">*</span></Form.Label>
                <DatePickerField
                  id="expected_date"
                  name="expected_date"
                  value={formik.values.expected_date}
                  onChange={handleExpectedDateChange}
                  onBlur={() => formik.setFieldTouched('expected_date', true)}
                  isInvalid={formik.touched.expected_date && formik.errors.expected_date}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.expected_date}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col sm={4} className="mb-3">
              <Form.Group controlId="status">
                <Form.Label>Status<span className="text-danger">*</span></Form.Label>
                <FormikSearchableSelect
                  formik={formik}
                  name="status"
                  options={statusOptions}
                  placeholder="Search status..."
                />
              </Form.Group>
            </Col>

            <Col sm={12} className="mb-3">
              <Form.Group controlId="remarks">
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  type="text"
                  as="textarea"
                  rows={3}
                  {...formik.getFieldProps("remarks")}
                  isInvalid={formik.touched.remarks && formik.errors.remarks}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.remarks}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

          </Row>

          <div className="mt-3">
            <Button 
              variant="primary" 
              type="submit"
              disabled={formik.isSubmitting}
            >
              Save
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};